import React from 'react'

import type { ControllerRenderProps, FieldValues } from 'react-hook-form'
import { useIntl } from 'react-intl'
import type { ActionMeta, GroupBase, InputActionMeta, InputProps, SingleValue } from 'react-select'
import Select, { components as reactSelectComponents } from 'react-select'

import { selectFormStyles } from '@styles'
import { recommendationMessages } from '@messages'

function transformToSelectValue(value: Nullable<string>) {
  return value != null && value !== '' ? { label: value, value } : null
}

type Option = { label: string; value: string }

const ReactSelectInput = reactSelectComponents.Input as unknown as React.ComponentType<
  InputProps<Option, false, GroupBase<Option>>
>

// customize react-select Input component to avoid hidden state (will set opacity 0 on input)
function SelectInput({ isHidden, ...props }: InputProps<Option, false, GroupBase<Option>>) {
  return <ReactSelectInput isHidden={false} {...props} />
}

const componentsOverrides = {
  Input: SelectInput,
}

const noOptionsMessage = () => null

interface RecommendationSelectProps {
  id?: string
  className?: string
  field: Pick<ControllerRenderProps<FieldValues, string>, 'value' | 'onChange'>
  disabled?: boolean
  recommendations: string[]
  transform?: ReactHookFormTransform
}

export function RecommendationSelect({
  id,
  className,
  field: { value, onChange },
  disabled,
  recommendations,
  transform,
}: RecommendationSelectProps) {
  const { formatMessage } = useIntl()

  const options = React.useMemo(
    () => [
      {
        label: formatMessage(recommendationMessages.recommendationsLabel),
        options: recommendations.map(value => ({ value, label: value })),
      },
    ],
    [formatMessage, recommendations]
  )

  const onInputChangeHandler = React.useCallback(
    (value: string, { action }: InputActionMeta) => {
      if (action === 'input-change') {
        onChange(value === '' ? null : transform ? transform.output(value) : value)
      }
    },
    [onChange, transform]
  )

  const onChangeHandler = React.useCallback(
    (option: SingleValue<Option>, { action }: ActionMeta<Option>) => {
      switch (action) {
        case 'clear':
        case 'remove-value':
          onChange(null)
          break
        case 'select-option':
          onChange(option?.value)
          break
        default:
      }
    },
    [onChange]
  )

  const inputValue = value ?? ''

  return (
    <Select
      id={id}
      classNamePrefix="react-select"
      className={className}
      inputValue={transform ? transform.input(inputValue) : inputValue}
      onInputChange={onInputChangeHandler}
      value={transformToSelectValue(value)}
      onChange={onChangeHandler}
      options={options}
      styles={selectFormStyles}
      isClearable
      isDisabled={disabled}
      placeholder={null}
      noOptionsMessage={noOptionsMessage}
      components={componentsOverrides}
    />
  )
}
